import React, { useEffect, useState, useRef } from "react";
import { motion, Reorder, useDragControls } from "framer-motion";
import { Plus, Trash2, Save, Users as UsersIcon, ListChecks, SlidersHorizontal, GripVertical, HardHat, X, Mail, Sparkles, AlertTriangle, ClipboardList, Pencil, Crown } from "lucide-react";import api, { apiError, publicFileUrl } from "@/lib/api";
import OnboardingFormEditor from "@/components/OnboardingFormEditor";
import { useAuth } from "@/context/AuthContext";
import { ResetPasswordButton } from "@/components/ResetPasswordButton";
import { fmtDateTime } from "@/lib/helpers";
import { STAGES, ROLE_LABELS } from "@/lib/helpers";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const DEFAULT_FEAT = { holiday_rentals: true, long_term: false, investor_matching: true, portal_syndication: true, public_issue_link: true };

const ROLES = ["admin", "operations", "compliance", "landlord"];

function UsersTab() {
  const { user: me, setUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "operations" });

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/users", form); toast.success("User created"); setOpen(false); setForm({ name: "", email: "", password: "", role: "operations" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const changeRole = async (u, role) => {
    try { await api.put(`/users/${u.id}`, { role }); toast.success(`${u.name} → ${ROLE_LABELS[role]}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (u) => {
    try { await api.delete(`/users/${u.id}`); toast.success("User removed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const anySuper = users.some((u) => u.is_super_admin);
  const canGrantSuper = me?.is_super_admin || !anySuper;
  const toggleSuper = async (u) => {
    try {
      await api.put(`/users/${u.id}/super-admin`, { value: !u.is_super_admin });
      toast.success(u.is_super_admin ? `${u.name} is no longer a super-admin` : `${u.name} is now a super-admin`);
      if (u.id === me?.id) setUser({ ...me, is_super_admin: !u.is_super_admin });
      load();
    }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="bg-white border border-border rounded-md">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <span className="overline">Staff & Owner Accounts</span>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm" data-testid="add-user-button"><Plus className="h-4 w-4 mr-1" />Add User</Button></DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="font-head">New User</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="overline">Name</Label><Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
              <div><Label className="overline">Email</Label><Input data-testid="user-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" /></div>
              <div><Label className="overline">Temporary password</Label><Input data-testid="user-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" /></div>
              <div>
                <Label className="overline">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button data-testid="save-user-button" onClick={create} disabled={!form.name || !form.email || !form.password}>Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="border-b border-border bg-secondary/40 text-left">
          <th className="px-5 py-3 overline font-semibold">Name</th>
          <th className="px-5 py-3 overline font-semibold">Email</th>
          <th className="px-5 py-3 overline font-semibold w-44">Role</th>
          <th className="px-5 py-3 overline font-semibold">Last sign-in</th>
          <th className="px-5 py-3"></th>
        </tr></thead>
        <tbody className="divide-y divide-border">
          {users.map((u) => (
            <tr key={u.id} data-testid={`user-row-${u.id}`}>
              <td className="px-5 py-3 font-medium">
                {u.name}
                {u.is_super_admin && <span data-testid={`super-badge-${u.id}`} className="ml-2 inline-flex items-center gap-1 text-[0.6rem] px-1.5 py-0.5 rounded-full bg-[#C6A87C]/20 text-[#8a6d3b] align-middle"><Crown className="h-2.5 w-2.5" />Super-admin</span>}
                {u.role === "admin" && canGrantSuper && (
                  <button type="button" data-testid={`toggle-super-${u.id}`} onClick={() => toggleSuper(u)} className="block text-[0.65rem] text-muted-foreground underline underline-offset-2 hover:text-foreground mt-0.5">
                    {u.is_super_admin ? "Remove super-admin" : "Make super-admin"}
                  </button>
                )}
              </td>
              <td className="px-5 py-3 text-muted-foreground">{u.email}</td>
              <td className="px-5 py-3">
                <Select value={u.role} onValueChange={(v) => changeRole(u, v)} disabled={u.role === "admin" && !me?.is_super_admin}>
                  <SelectTrigger data-testid={`user-role-${u.id}`} className="h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
                </Select>
              </td>
              <td data-testid={`user-last-login-${u.id}`} className="px-5 py-3 text-xs text-muted-foreground whitespace-nowrap">
                {u.last_login_at ? fmtDateTime(u.last_login_at) : "Never"}
                {u.must_change_password && <span className="block text-[0.65rem] text-[#C6A87C]">Temporary password</span>}
              </td>
              <td className="px-5 py-3 text-right whitespace-nowrap">
                {u.id !== me?.id && <ResetPasswordButton endpoint={`/users/${u.id}/reset-password`} name={u.name} testId={`reset-pwd-user-${u.id}`} />}
                {(u.role !== "admin" || me?.is_super_admin) && u.id !== me?.id && <Button variant="ghost" size="icon" data-testid={`delete-user-${u.id}`} onClick={() => remove(u)}><Trash2 className="h-4 w-4 text-[#9E382A]" /></Button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TemplateTab() {
  const [items, setItems] = useState([]);
  const [staff, setStaff] = useState([]);
  const load = () => api.get("/checklist-template").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/staff").then((r) => setStaff(r.data)).catch(() => {}); }, []);

  const upd = (i, key, val) => setItems((prev) => prev.map((it, idx) => idx === i ? { ...it, [key]: val } : it));
  const addStep = () => setItems((prev) => [...prev, { stage: STAGES[0], name: "New step", description: "", owner_role: "operations", requires_document: false, has_expiry: false, requires_signature: false, assignee: "", assignees: [], manual_assignees: [], bides_supplies: false }]);
  const remove = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i));
  const save = async () => {
    try {
      const payload = items.map((it) => ({ stage: it.stage, name: it.name, description: it.description || "", owner_role: it.owner_role, requires_document: !!it.requires_document, has_expiry: !!it.has_expiry, requires_signature: !!it.requires_signature, assignee: it.assignee || "", assignees: it.assignees || [], manual_assignees: it.manual_assignees || [], bides_supplies: !!it.bides_supplies }));
      await api.put("/checklist-template", payload);
      toast.success("Checklist template saved");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Steps here apply to every <strong>new</strong> property onboarding. Existing properties keep their current checklist.</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" data-testid="add-step-button" onClick={addStep}><Plus className="h-4 w-4 mr-1" />Add Step</Button>
          <Button size="sm" data-testid="save-template-button" onClick={save}><Save className="h-4 w-4 mr-1" />Save Template</Button>
        </div>
      </div>
      <div className="space-y-3">
        {items.map((it, i) => (
          <div key={i} data-testid={`template-step-${i}`} className="bg-white border border-border rounded-md p-4">
            <div className="flex items-start gap-3">
              <GripVertical className="h-5 w-5 text-muted-foreground mt-2 shrink-0" />
              <div className="flex-1 grid lg:grid-cols-2 gap-3">
                <div>
                  <Label className="overline">Step name</Label>
                  <Input value={it.name} onChange={(e) => upd(i, "name", e.target.value)} className="mt-1.5" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="overline">Stage</Label>
                    <Select value={it.stage} onValueChange={(v) => upd(i, "stage", v)}>
                      <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="overline">Owner</Label>
                    <Select value={it.owner_role} onValueChange={(v) => upd(i, "owner_role", v)}>
                      <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="lg:col-span-2">
                  <Label className="overline">Description</Label>
                  <Input value={it.description || ""} onChange={(e) => upd(i, "description", e.target.value)} className="mt-1.5" />
                </div>
                <div>
                  <Label className="overline">Assignee (name)</Label>
                  <Input data-testid={`template-assignee-${i}`} value={it.assignee || ""} placeholder="e.g. Alison" onChange={(e) => upd(i, "assignee", e.target.value)} className="mt-1.5" />
                </div>
                <div>
                  <Label className="overline">Assign staff (default)</Label>
                  <Select value="" onValueChange={(uid) => { const cur = it.assignees || []; upd(i, "assignees", cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid]); }}>
                    <SelectTrigger data-testid={`template-staff-${i}`} className="mt-1.5"><span className="text-muted-foreground text-sm">+ Assign staff</span></SelectTrigger>
                    <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{(it.assignees || []).includes(s.id) ? "✓ " : ""}{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                  {(it.assignees || []).length > 0 && <div className="flex flex-wrap gap-1 mt-1.5">{(it.assignees || []).map((uid) => { const nm = (staff.find((s) => s.id === uid) || {}).name || "Staff"; return <span key={uid} data-testid={`template-staff-chip-${i}-${uid}`} className="text-[0.6rem] px-2 py-0.5 rounded-full bg-[#C6A87C]/20 text-[#8a6d3b] inline-flex items-center gap-1">{nm}<button onClick={() => upd(i, "assignees", (it.assignees || []).filter((x) => x !== uid))}><X className="h-2.5 w-2.5" /></button></span>; })}</div>}
                </div>
                <div className="lg:col-span-2 flex flex-wrap gap-6">
                  <label className="flex items-center gap-2 text-sm"><Switch checked={!!it.requires_document} onCheckedChange={(v) => upd(i, "requires_document", v)} />Requires document</label>
                  <label className="flex items-center gap-2 text-sm"><Switch checked={!!it.has_expiry} onCheckedChange={(v) => upd(i, "has_expiry", v)} />Has expiry</label>
                  <label className="flex items-center gap-2 text-sm"><Switch checked={!!it.requires_signature} onCheckedChange={(v) => upd(i, "requires_signature", v)} />Requires signature</label>
                  <label className="flex items-center gap-2 text-sm"><Switch data-testid={`template-bides-${i}`} checked={!!it.bides_supplies} onCheckedChange={(v) => upd(i, "bides_supplies", v)} />Bides supplies</label>
                </div>
              </div>
              <Button variant="ghost" size="icon" data-testid={`remove-step-${i}`} onClick={() => remove(i)}><Trash2 className="h-4 w-4 text-[#9E382A]" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  const [newAmenity, setNewAmenity] = useState("");
  const loaded = useRef(false);
  useEffect(() => { api.get("/settings").then((r) => { if (loaded.current) return; loaded.current = true; setS(r.data); }); }, []);
  if (!s) return <div className="overline text-muted-foreground">Loading…</div>;

  const save = async () => {
    try { const { data } = await api.put("/settings", { brand_name: s.brand_name, default_commission: Number(s.default_commission), onboarding_reminder_days: Number(s.onboarding_reminder_days) || 7, onboarding_reminder_max: Number(s.onboarding_reminder_max) || 3, portals: s.portals, features: s.features, amenities: s.amenities || [], cleaning_items: s.cleaning_items || [], logo: s.logo || "", favicon: s.favicon || "", hero_paragraph: s.hero_paragraph || "", hero_overline: s.hero_overline || "", hero_title: s.hero_title || "", hero_image: s.hero_image || "", hero_badge: s.hero_badge || "", hero_caption: s.hero_caption || "", footer_text: s.footer_text || "", seo_title: s.seo_title || "", seo_description: s.seo_description || "", seo_keywords: s.seo_keywords || "", smtp_host: s.smtp_host || "", smtp_port: s.smtp_port || "", smtp_user: s.smtp_user || "", smtp_password: s.smtp_password || "", smtp_from: s.smtp_from || "", office_phone: s.office_phone || "", owner_cta_text: s.owner_cta_text || "", proposal_reminder_days: Number(s.proposal_reminder_days) || 7, proposal_reminder_max: Number(s.proposal_reminder_max) >= 0 ? Number(s.proposal_reminder_max) : 3, proposal_auto_convert: s.proposal_auto_convert !== false, proposal_send_onboarding: s.proposal_send_onboarding !== false, email_templates: s.email_templates || {} }); setS(data); toast.success("Settings saved"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const testEmail = async () => {
    try {
      const { data } = await api.post("/settings/test-email", {});
      if (data.ok) toast.success(`Test email sent via ${data.transport.toUpperCase()} to ${data.sent_to}`);
      else toast.error(`${data.transport.toUpperCase()} send failed: ${data.error || "unknown error"}`);
    } catch (e) { toast.error(apiError(e)); }
  };
  const onLogo = (e) => { const f = e.target.files[0]; if (!f) return; const r = new FileReader(); r.onload = () => setS({ ...s, logo: r.result }); r.readAsDataURL(f); };
  const onHero = (e) => { const f = e.target.files[0]; if (!f) return; const r = new FileReader(); r.onload = () => setS({ ...s, hero_image: r.result }); r.readAsDataURL(f); };
  const onFavicon = (e) => { const f = e.target.files[0]; if (!f) return; const r = new FileReader(); r.onload = () => setS({ ...s, favicon: r.result }); r.readAsDataURL(f); };
  const amenities = s.amenities || [];
  const addAmenity = () => { if (newAmenity.trim() && !amenities.includes(newAmenity.trim())) { setS({ ...s, amenities: [...amenities, newAmenity.trim()] }); setNewAmenity(""); } };
  const removeAmenity = (a) => setS({ ...s, amenities: amenities.filter((x) => x !== a) });

  return (
    <div className="max-w-xl space-y-5 bg-white border border-border rounded-md p-6">
      <div>
        <Label className="overline">Brand name</Label>
        <Input data-testid="brand-name-input" value={s.brand_name} onChange={(e) => setS({ ...s, brand_name: e.target.value })} className="mt-1.5" />
      </div>
      <div>
        <Label className="overline">Logo (business-facing, shown on login)</Label>
        <div className="flex items-center gap-3 mt-1.5">
          {s.logo && <img src={s.logo.startsWith("data:") ? s.logo : publicFileUrl(s.logo, s.branding_rev)} alt="logo" className="h-10 max-w-[140px] object-contain border border-border rounded p-1 bg-white" />}
          <Input data-testid="logo-upload-input" type="file" accept="image/*" onChange={onLogo} />
          {s.logo && <Button variant="ghost" size="sm" data-testid="logo-remove" onClick={() => setS({ ...s, logo: "" })}>Remove</Button>}
        </div>
      </div>
      <div>
        <Label className="overline">Favicon (browser tab icon)</Label>
        <div className="flex items-center gap-3 mt-1.5">
          {s.favicon && <img src={s.favicon.startsWith("data:") ? s.favicon : publicFileUrl(s.favicon, s.branding_rev)} alt="favicon" data-testid="favicon-preview" className="h-8 w-8 object-contain border border-border rounded p-1 bg-white" />}
          <Input data-testid="favicon-upload-input" type="file" accept="image/png,image/x-icon,image/svg+xml,image/jpeg" onChange={onFavicon} />
          {s.favicon && <Button variant="ghost" size="sm" data-testid="favicon-remove" onClick={() => setS({ ...s, favicon: "" })}>Remove</Button>}
        </div>
      </div>
      <div>
        <Label className="overline">Login paragraph (business-facing)</Label>
        <Textarea data-testid="hero-paragraph-input" value={s.hero_paragraph || ""} onChange={(e) => setS({ ...s, hero_paragraph: e.target.value })} className="mt-1.5" rows={3} />
      </div>
      <div className="pt-2 border-t border-border space-y-4">
        <p className="overline">Front page content</p>
        <div>
          <Label className="overline">Hero overline (small label)</Label>
          <Input data-testid="hero-overline-input" value={s.hero_overline || ""} onChange={(e) => setS({ ...s, hero_overline: e.target.value })} className="mt-1.5" placeholder="Onboarding & Property CRM" />
        </div>
        <div>
          <Label className="overline">Hero headline (use new lines to wrap)</Label>
          <Textarea data-testid="hero-title-input" value={s.hero_title || ""} onChange={(e) => setS({ ...s, hero_title: e.target.value })} className="mt-1.5" rows={2} />
        </div>
        <div>
          <Label className="overline">Hero image (right panel)</Label>
          <div className="flex items-center gap-3 mt-1.5">
            {s.hero_image && <img src={s.hero_image.startsWith("data:") ? s.hero_image : publicFileUrl(s.hero_image, s.branding_rev)} alt="hero" className="h-12 w-20 object-cover border border-border rounded" />}
            <Input data-testid="hero-image-input" type="file" accept="image/*" onChange={onHero} />
            {s.hero_image && <Button variant="ghost" size="sm" data-testid="hero-image-remove" onClick={() => setS({ ...s, hero_image: "" })}>Remove</Button>}
          </div>
        </div>
        <div>
          <Label className="overline">Hero badge (label over image)</Label>
          <Input data-testid="hero-badge-input" value={s.hero_badge || ""} onChange={(e) => setS({ ...s, hero_badge: e.target.value })} className="mt-1.5" placeholder="Gatekeeper" />
        </div>
        <div>
          <Label className="overline">Hero caption (over image)</Label>
          <Textarea data-testid="hero-caption-input" value={s.hero_caption || ""} onChange={(e) => setS({ ...s, hero_caption: e.target.value })} className="mt-1.5" rows={2} />
        </div>
        <div>
          <Label className="overline">Footer line</Label>
          <Input data-testid="footer-text-input" value={s.footer_text || ""} onChange={(e) => setS({ ...s, footer_text: e.target.value })} className="mt-1.5" placeholder="Secured internal access" />
        </div>
        <div>
          <Label className="overline">Front-page owner CTA</Label>
          <Input data-testid="owner-cta-input" value={s.owner_cta_text || ""} onChange={(e) => setS({ ...s, owner_cta_text: e.target.value })} className="mt-1.5" placeholder="Own a holiday property? Let us manage it." />
          <p className="text-xs text-muted-foreground mt-1">Headline for the "Register your property" call-to-action shown to owners on the front page.</p>
        </div>
      </div>
      <div className="pt-2 border-t border-border space-y-4">
        <p className="overline">SEO & Page Title</p>
        <div>
          <Label className="overline">Page title (browser tab & search)</Label>
          <Input data-testid="seo-title-input" value={s.seo_title || ""} onChange={(e) => setS({ ...s, seo_title: e.target.value })} className="mt-1.5" placeholder="Startup Agency" />
        </div>
        <div>
          <Label className="overline">Meta description</Label>
          <Textarea data-testid="seo-description-input" value={s.seo_description || ""} onChange={(e) => setS({ ...s, seo_description: e.target.value })} className="mt-1.5" rows={2} />
        </div>
        <div>
          <Label className="overline">Keywords (comma separated)</Label>
          <Input data-testid="seo-keywords-input" value={s.seo_keywords || ""} onChange={(e) => setS({ ...s, seo_keywords: e.target.value })} className="mt-1.5" />
        </div>
      </div>
      <div>
        <Label className="overline">Default management commission (%)</Label>
        <Input data-testid="default-commission-input" type="number" value={s.default_commission} onChange={(e) => setS({ ...s, default_commission: e.target.value })} className="mt-1.5" />
        <p className="text-xs text-muted-foreground mt-1">Owner payout = income − this % (overridable per owner or property). No fixed monthly fee.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="overline">Onboarding reminder interval (days)</Label>
          <Select value={String(s.onboarding_reminder_days ?? 7)} onValueChange={(v) => setS({ ...s, onboarding_reminder_days: Number(v) })}>
            <SelectTrigger data-testid="reminder-interval-select" className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="3">Every 3 days</SelectItem>
              <SelectItem value="7">Every 7 days</SelectItem>
              <SelectItem value="14">Every 14 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="overline">Max reminders per landlord</Label>
          <Input data-testid="reminder-max-input" type="number" min="0" value={s.onboarding_reminder_max ?? 3} onChange={(e) => setS({ ...s, onboarding_reminder_max: e.target.value })} className="mt-1.5" />
          <p className="text-xs text-muted-foreground mt-1">Stops nudging once this many onboarding reminders have been sent.</p>
        </div>
      </div>
      <div className="pt-2 border-t border-border">
        <p className="overline mb-3">Modules & Features</p>
        <div className="space-y-3">
          {[
            ["holiday_rentals", "Holiday rentals", "Short-stay / guest lettings"],
            ["long_term", "Long-term tenancies", "Assured shorthold tenancies"],
            ["investor_matching", "Investor matching", "Suggest properties to investors"],
            ["portal_syndication", "Portal syndication", "Push listings to Rightmove / Zoopla"],
            ["public_issue_link", "Public issue link", "Guests report issues via QR / link"],
          ].map(([k, label, desc]) => {
            const on = (s.features || {})[k] ?? (k === "long_term" ? false : true);
            return (
              <div key={k} className="flex items-center justify-between">
                <div><p className="text-sm font-medium">{label}</p><p className="text-xs text-muted-foreground">{desc}</p></div>
                <Switch data-testid={`feature-${k}`} checked={on} onCheckedChange={(v) => setS({ ...s, features: { ...(s.features || DEFAULT_FEAT), [k]: v } })} />
              </div>
            );
          })}
        </div>
      </div>
      <div className="pt-2 border-t border-border">
        <p className="overline mb-3">Amenities master list</p>
        <div className="flex flex-wrap gap-2 mb-3">
          {amenities.map((a) => (
            <span key={a} data-testid={`amenity-chip-${a}`} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-secondary">{a}<button onClick={() => removeAmenity(a)}><X className="h-3 w-3" /></button></span>
          ))}
        </div>
        <div className="flex gap-2">
          <Input data-testid="amenity-input" value={newAmenity} onChange={(e) => setNewAmenity(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addAmenity()} placeholder="Add amenity…" />
          <Button variant="outline" data-testid="add-amenity-button" onClick={addAmenity}><Plus className="h-4 w-4" /></Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">These appear as checkboxes on each property.</p>
      </div>
      <div className="pt-2 border-t border-border">
        <p className="overline mb-3">Office phone</p>
        <Input data-testid="office-phone-input" value={s.office_phone || ""} onChange={(e) => setS({ ...s, office_phone: e.target.value })} placeholder="+44 20 1234 5678" />
        <p className="text-xs text-muted-foreground mt-2">Shown as a "Call office" button on the cleaner QR form so cleaners can reach you immediately. Leave blank to hide it.</p>
      </div>
      <div className="pt-2 border-t border-border">
        <p className="overline mb-3">Proposal reminder</p>
        <div className="flex items-center gap-2">
          <Input data-testid="proposal-reminder-days-input" type="number" min={1} className="w-24" value={s.proposal_reminder_days ?? 7} onChange={(e) => setS({ ...s, proposal_reminder_days: e.target.value })} />
          <span className="text-sm text-muted-foreground">days between nudges</span>
        </div>
        <div className="flex items-center gap-2 mt-2">
          <Input data-testid="proposal-reminder-max-input" type="number" min={0} className="w-24" value={s.proposal_reminder_max ?? 3} onChange={(e) => setS({ ...s, proposal_reminder_max: e.target.value })} />
          <span className="text-sm text-muted-foreground">max reminders (0 = unlimited)</span>
        </div>
        <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer">
          <input type="checkbox" data-testid="proposal-auto-convert-toggle" checked={s.proposal_auto_convert !== false} onChange={(e) => setS({ ...s, proposal_auto_convert: e.target.checked })} className="h-4 w-4 accent-[#2B4C3F]" />
          Auto-create the Owner + a draft property when a proposal is accepted
        </label>
        <label className="flex items-center gap-2 mt-2 text-sm cursor-pointer">
          <input type="checkbox" data-testid="proposal-send-onboarding-toggle" checked={s.proposal_send_onboarding !== false} onChange={(e) => setS({ ...s, proposal_send_onboarding: e.target.checked })} className="h-4 w-4 accent-[#2B4C3F]" />
          Email the new owner the onboarding form immediately on accept
        </label>
        <p className="text-xs text-muted-foreground mt-2">Owner leads with a sent proposal and no response are re-nudged by email up to the max, then left alone.</p>
      </div>
      <div className="pt-2 border-t border-border">
        <p className="overline mb-3">Syndication portals</p>
        <label className="flex items-center justify-between py-2"><span className="text-sm">Rightmove</span><Switch data-testid="portal-rightmove" checked={!!s.portals?.rightmove} onCheckedChange={(v) => setS({ ...s, portals: { ...s.portals, rightmove: v } })} /></label>
        <label className="flex items-center justify-between py-2"><span className="text-sm">Zoopla</span><Switch data-testid="portal-zoopla" checked={!!s.portals?.zoopla} onCheckedChange={(v) => setS({ ...s, portals: { ...s.portals, zoopla: v } })} /></label>
        <p className="text-xs text-muted-foreground mt-2">Live datafeed (Rightmove/Zoopla) push is simulated until a datafeed agreement + credentials are provided.</p>
      </div>
      <div className="pt-2 border-t border-border">
        <p className="overline mb-3">SMTP — your own mail server</p>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="overline">Host</Label><Input data-testid="smtp-host-input" value={s.smtp_host || ""} onChange={(e) => setS({ ...s, smtp_host: e.target.value })} className="mt-1.5" placeholder="smtp.example.com" /></div>
          <div><Label className="overline">Port</Label><Input data-testid="smtp-port-input" value={s.smtp_port || ""} onChange={(e) => setS({ ...s, smtp_port: e.target.value })} className="mt-1.5" placeholder="587" /></div>
          <div><Label className="overline">Username</Label><Input data-testid="smtp-user-input" value={s.smtp_user || ""} onChange={(e) => setS({ ...s, smtp_user: e.target.value })} className="mt-1.5" /></div>
          <div><Label className="overline">Password</Label><Input data-testid="smtp-pass-input" type="password" value={s.smtp_password || ""} onChange={(e) => setS({ ...s, smtp_password: e.target.value })} className="mt-1.5" placeholder={s.smtp_password_set ? "•••••• (saved — leave blank to keep)" : ""} /></div>
          <div className="col-span-2"><Label className="overline">From address</Label><Input data-testid="smtp-from-input" value={s.smtp_from || ""} onChange={(e) => setS({ ...s, smtp_from: e.target.value })} className="mt-1.5" placeholder="lettings@bides.9pm.co.uk" /></div>
        </div>
        <p className="text-xs text-muted-foreground mt-2">When a Host is set, all emails send through your SMTP server. Leave Host blank to use the managed service. Save your settings before sending a test.</p>
        <Button variant="outline" size="sm" className="mt-3" data-testid="send-test-email-button" onClick={testEmail}><Mail className="h-4 w-4 mr-1" />Send test email</Button>
      </div>
      <Button data-testid="save-settings-button" onClick={save}><Save className="h-4 w-4 mr-1" />Save Settings</Button>
    </div>
  );
}

const EMPTY_CLEANER = { name: "", email: "", phone: "", company: "", notes: "" };
function CleanersDirectory() {
  const [list, setList] = useState([]);
  const [f, setF] = useState(null);
  const load = () => api.get("/cleaners").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    const body = { ...f, email: f.email.trim() || null };
    try {
      if (f.id) await api.put(`/cleaners/${f.id}`, body); else await api.post("/cleaners", body);
      toast.success("Cleaner saved"); setF(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (c) => { try { await api.delete(`/cleaners/${c.id}`); load(); } catch (e) { toast.error(apiError(e)); } };
  const field = (k, label, tid) => (
    <div><Label className="overline">{label}</Label><Input data-testid={tid} value={f?.[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1.5" /></div>
  );
  return (
    <div className="bg-white border border-border rounded-md mt-5">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <span className="overline">Cleaner Directory</span>
        <Button size="sm" data-testid="add-cleaner-button" onClick={() => setF({ ...EMPTY_CLEANER })}><Plus className="h-4 w-4 mr-1" />Add Cleaner</Button>
      </div>
      <Dialog open={!!f} onOpenChange={(v) => { if (!v) setF(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-head">{f?.id ? "Edit Cleaner" : "New Cleaner"}</DialogTitle><DialogDescription>Saved cleaners can be picked on any property's cleaning card and onboarding form.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            {field("name", "Name", "cleaner-dir-name")}
            <div className="grid grid-cols-2 gap-3">{field("email", "Email", "cleaner-dir-email")}{field("phone", "Phone", "cleaner-dir-phone")}</div>
            {field("company", "Company", "cleaner-dir-company")}
            {field("notes", "Notes", "cleaner-dir-notes")}
          </div>
          <DialogFooter><Button data-testid="save-cleaner-dir-button" onClick={save} disabled={!f?.name?.trim()}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {list.length === 0 ? <p className="p-8 text-center text-sm text-muted-foreground">No cleaners saved yet.</p> : (
        <table className="w-full text-sm"><tbody className="divide-y divide-border">
          {list.map((c) => (
            <tr key={c.id} data-testid={`cleaner-row-${c.id}`}>
              <td className="px-5 py-3 font-medium">{c.name}</td>
              <td className="px-5 py-3 text-muted-foreground">{c.company}</td>
              <td className="px-5 py-3 text-muted-foreground">{[c.email, c.phone].filter(Boolean).join(" · ")}</td>
              <td className="px-5 py-3 text-right whitespace-nowrap">
                <Button variant="ghost" size="icon" data-testid={`edit-cleaner-${c.id}`} onClick={() => setF({ ...EMPTY_CLEANER, ...c, email: c.email || "" })}><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" data-testid={`del-cleaner-${c.id}`} onClick={() => remove(c)}><Trash2 className="h-4 w-4 text-[#9E382A]" /></Button>
              </td>
            </tr>
          ))}
        </tbody></table>
      )}
    </div>
  );
}

function ContractorsTab() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", phone: "", trade: "", notes: "" });
  const load = () => api.get("/contractors").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { await api.post("/contractors", f); toast.success("Contractor saved"); setOpen(false); setF({ name: "", email: "", phone: "", trade: "", notes: "" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (c) => { try { await api.delete(`/contractors/${c.id}`); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="bg-white border border-border rounded-md">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <span className="overline">Contractor Directory</span>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm" data-testid="add-contractor-button"><Plus className="h-4 w-4 mr-1" />Add Contractor</Button></DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="font-head">New Contractor</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="overline">Name</Label><Input data-testid="contractor-dir-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="overline">Email</Label><Input data-testid="contractor-dir-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1.5" /></div>
                <div><Label className="overline">Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="mt-1.5" /></div>
              </div>
              <div><Label className="overline">Trade</Label><Input value={f.trade} onChange={(e) => setF({ ...f, trade: e.target.value })} className="mt-1.5" placeholder="Plumbing, Electrical…" /></div>
              <div><Label className="overline">Notes</Label><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="mt-1.5" /></div>
            </div>
            <DialogFooter><Button data-testid="save-contractor-button" onClick={create} disabled={!f.name || !f.email}>Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {list.length === 0 ? <p className="p-8 text-center text-sm text-muted-foreground">No contractors saved yet.</p> : (
        <table className="w-full text-sm"><tbody className="divide-y divide-border">
          {list.map((c) => (
            <tr key={c.id} data-testid={`contractor-row-${c.id}`}>
              <td className="px-5 py-3 font-medium">{c.name}</td>
              <td className="px-5 py-3 text-muted-foreground">{c.trade}</td>
              <td className="px-5 py-3 text-muted-foreground">{c.email}{c.phone ? ` · ${c.phone}` : ""}</td>
              <td className="px-5 py-3 text-right"><Button variant="ghost" size="icon" data-testid={`del-contractor-${c.id}`} onClick={() => remove(c)}><Trash2 className="h-4 w-4 text-[#9E382A]" /></Button></td>
            </tr>
          ))}
        </tbody></table>
      )}
    </div>
  );
}

const EMAIL_KEYS = [
  ["onboarding_invite", "Onboarding invite", "{{owner_name}}, {{property}}, {{link}}"],
  ["onboarding_reminder", "Onboarding reminder", "{{owner_name}}, {{property}}, {{link}}"],
  ["signup_confirmation", "Owner signup confirmation", "{{owner_name}}, {{brand}}"],
  ["document_expiry", "Document expiry reminder", "{{owner_name}}, {{doc_type}}, {{property}}, {{when}}"],
  ["cleaning_link", "Cleaner checklist link", "{{cleaner_name}}, {{property}}, {{link}}"],
  ["proposal_email", "Owner proposal", "{{name}}, {{link}}, {{message}}"],
  ["proposal_reminder", "Proposal reminder", "{{name}}, {{link}}"],
  ["password_reset", "Password reset", "{{name}}, {{link}}"],
  ["issue_alert", "Issue alert", "{{reporter}}, {{property}}, {{description}}, {{contact}}"],
  ["onboarding_change_alert", "Owner change alert (to staff)", "{{name}}, {{owner_name}}, {{property}}, {{count}}, {{changes}}, {{link}}"],
  ["portal_invite", "Owner portal invite", "{{name}}, {{email}}, {{link}}"],
];
function EmailsTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <div className="overline text-muted-foreground">Loading…</div>;
  const t = s.email_templates || {};
  const defs = s.email_template_defaults || {};
  const eff = (key, field) => (t[key] || {})[field] || (defs[key] || {})[field] || "";
  const isCustom = (key) => { const o = t[key] || {}; const d = defs[key] || {}; return (!!o.subject && o.subject !== d.subject) || (!!o.body && o.body !== d.body); };
  const upd = (key, field, val) => setS({ ...s, email_templates: { ...t, [key]: { ...(t[key] || {}), [field]: val } } });
  const reset = (key) => { const nt = { ...t }; delete nt[key]; setS({ ...s, email_templates: nt }); };
  const save = async () => {
    try { const { data } = await api.put("/settings", { email_templates: s.email_templates || {} }); setS(data); toast.success("Email templates saved"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const sendTest = async (key) => {
    try {
      const { data } = await api.post("/settings/test-template", { key });
      if (data.ok) toast.success(`Test "${key}" sent to ${data.sent_to}`);
      else toast.warning(`Test not delivered (${data.error || "unknown error"}) — check your email transport in Global settings.`);
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Every email below is pre-filled with the exact text currently being sent. Edit to customise it (badge turns to "Customised"), or use "Reset to default". Placeholders are filled in automatically. "Send test to me" delivers a sample to your own inbox.</p>
        <Button size="sm" data-testid="save-emails-button" onClick={save}><Save className="h-4 w-4 mr-1" />Save Emails</Button>
      </div>
      {EMAIL_KEYS.map(([key, label, vars]) => (
        <div key={key} data-testid={`email-tmpl-${key}`} className="bg-white border border-border rounded-md p-4 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <p className="overline">{label}</p>
              <span data-testid={`email-status-${key}`} className={`text-[0.6rem] px-1.5 py-0.5 rounded-full ${isCustom(key) ? "bg-[#D98324]/15 text-[#D98324]" : "bg-[#2B4C3F]/12 text-[#2B4C3F]"}`}>{isCustom(key) ? "Customised" : "Default"}</span>
            </div>
            <div className="flex items-center gap-2">
              {isCustom(key) && <Button size="sm" variant="ghost" data-testid={`email-reset-${key}`} onClick={() => reset(key)}>Reset to default</Button>}
              <Button size="sm" variant="outline" data-testid={`email-test-${key}`} onClick={() => sendTest(key)}><Mail className="h-3.5 w-3.5 mr-1" />Send test to me</Button>
            </div>
          </div>
          <Input data-testid={`email-subject-${key}`} value={eff(key, "subject")} onChange={(e) => upd(key, "subject", e.target.value)} placeholder="Subject" />
          <Textarea data-testid={`email-body-${key}`} rows={4} value={eff(key, "body")} onChange={(e) => upd(key, "body", e.target.value)} placeholder="Message body" />
          <p className="text-xs text-muted-foreground">Placeholders: {vars}</p>
        </div>
      ))}
    </div>
  );
}

function RoomEditor({ room, onChange, onRemove }) {
  const controls = useDragControls();
  const [newItem, setNewItem] = useState("");
  const items = room.items || [];
  const addItem = () => { const v = newItem.trim(); if (v && !items.includes(v)) { onChange({ ...room, items: [...items, v] }); setNewItem(""); } };
  const removeItem = (i) => onChange({ ...room, items: items.filter((x) => x !== i) });
  return (
    <Reorder.Item value={room} dragListener={false} dragControls={controls} data-testid={`room-editor-${room.id}`} className="bg-white border border-border rounded-md p-4 space-y-3 list-none">
      <div className="flex items-center gap-2">
        <span onPointerDown={(e) => controls.start(e)} data-testid={`drag-lib-room-${room.id}`} className="cursor-grab active:cursor-grabbing touch-none text-muted-foreground shrink-0"><GripVertical className="h-4 w-4" /></span>
        <Input data-testid={`room-name-${room.id}`} value={room.name} onChange={(e) => onChange({ ...room, name: e.target.value })} className="font-medium" placeholder="Room name (e.g. Kitchen)" />
        <Button variant="ghost" size="icon" data-testid={`del-room-${room.id}`} onClick={onRemove}><Trash2 className="h-4 w-4 text-[#9E382A]" /></Button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((i) => (
          <span key={i} data-testid={`room-item-${room.id}-${i}`} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-secondary">{i}<button onClick={() => removeItem(i)}><X className="h-3 w-3" /></button></span>
        ))}
        {items.length === 0 && <span className="text-xs text-muted-foreground">No items yet.</span>}
      </div>
      <div className="flex gap-2">
        <Input data-testid={`room-item-input-${room.id}`} value={newItem} onChange={(e) => setNewItem(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addItem()} placeholder="Add task to this room…" />
        <Button variant="outline" data-testid={`add-room-item-${room.id}`} onClick={addItem}><Plus className="h-4 w-4" /></Button>
      </div>
    </Reorder.Item>
  );
}

function PresetEditor({ preset, library, onChange, onRemove }) {
  const qtyOf = (rid) => (preset.rooms || []).find((r) => r.room_id === rid)?.qty || 0;
  const setQty = (rid, qty) => {
    qty = Math.max(0, parseInt(qty, 10) || 0);
    const others = (preset.rooms || []).filter((r) => r.room_id !== rid);
    onChange({ ...preset, rooms: qty > 0 ? [...others, { room_id: rid, qty }] : others });
  };
  const total = (preset.rooms || []).reduce((n, r) => n + (r.qty || 0), 0);
  const libIds = new Set(library.map((l) => l.id));
  const orphans = (preset.rooms || []).filter((r) => r.room_id && !libIds.has(r.room_id));
  const dropOrphan = (rid) => onChange({ ...preset, rooms: (preset.rooms || []).filter((r) => r.room_id !== rid) });
  return (
    <div data-testid={`preset-editor-${preset.id}`} className="bg-white border border-border rounded-md p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Input data-testid={`preset-name-${preset.id}`} value={preset.name} onChange={(e) => onChange({ ...preset, name: e.target.value })} className="font-medium" placeholder="Preset name (e.g. 2-bed apartment)" />
        {orphans.length > 0 && <span data-testid={`preset-warning-${preset.id}`} className="inline-flex items-center gap-1 text-[0.65rem] px-2 py-0.5 rounded-full bg-[#D98324]/15 text-[#D98324] whitespace-nowrap"><AlertTriangle className="h-3 w-3" />Needs update</span>}
        <span className="text-xs text-muted-foreground whitespace-nowrap">{total} room{total === 1 ? "" : "s"}</span>
        <Button variant="ghost" size="icon" data-testid={`del-preset-${preset.id}`} onClick={onRemove}><Trash2 className="h-4 w-4 text-[#9E382A]" /></Button>
      </div>
      {orphans.length > 0 && (
        <div data-testid={`preset-orphans-${preset.id}`} className="bg-[#D98324]/[0.06] border border-[#D98324]/25 rounded-md p-2.5 space-y-1.5">
          <p className="text-[0.7rem] text-[#D98324] font-medium flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{orphans.length} room{orphans.length === 1 ? "" : "s"} no longer in your library</p>
          {orphans.map((r) => (
            <div key={r.room_id} className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="flex-1 truncate italic">Removed room · qty {r.qty}</span>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-[0.65rem]" data-testid={`preset-drop-orphan-${preset.id}-${r.room_id}`} onClick={() => dropOrphan(r.room_id)}>Remove</Button>
            </div>
          ))}
        </div>
      )}
      {library.length === 0 ? (
        <p className="text-xs text-muted-foreground">Add rooms to your library first.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {library.map((l) => (
            <div key={l.id} className="flex items-center gap-2">
              <span className="text-sm flex-1 truncate">{l.name}</span>
              <Input type="number" min={0} data-testid={`preset-qty-${preset.id}-${l.id}`} value={qtyOf(l.id)} onChange={(e) => setQty(l.id, e.target.value)} className="w-16 h-8" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CleaningTab() {
  const [rooms, setRooms] = useState(null);
  const [presets, setPresets] = useState([]);
  const [newRoom, setNewRoom] = useState("");
  const [newPreset, setNewPreset] = useState("");
  const nid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()));
  useEffect(() => { api.get("/settings").then((r) => { setRooms(r.data.cleaning_rooms || []); setPresets(r.data.cleaning_presets || []); }).catch(() => setRooms([])); }, []);
  if (rooms === null) return <div className="overline text-muted-foreground">Loading…</div>;
  const addRoom = () => {
    const name = newRoom.trim() || "New room";
    setRooms([...rooms, { id: nid(), name, items: [] }]);
    setNewRoom("");
  };
  const updateRoom = (idx, r) => setRooms(rooms.map((x, i) => (i === idx ? r : x)));
  const removeRoom = (idx) => setRooms(rooms.filter((_, i) => i !== idx));
  const save = async () => {
    const clean = rooms.map((r) => ({ id: r.id, name: (r.name || "").trim(), items: (r.items || []).filter((x) => x && x.trim()) })).filter((r) => r.name && r.items.length);
    const dropped = rooms.length - clean.length;
    try { const { data } = await api.put("/settings", { cleaning_rooms: clean }); setRooms(data.cleaning_rooms || clean); toast.success(dropped > 0 ? `Cleaning rooms saved (${dropped} empty room${dropped > 1 ? "s" : ""} skipped — add a name and at least one task)` : "Cleaning rooms saved"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const addPreset = () => { setPresets([...presets, { id: nid(), name: newPreset.trim() || "New preset", rooms: [] }]); setNewPreset(""); };
  const updatePreset = (idx, p) => setPresets(presets.map((x, i) => (i === idx ? p : x)));
  const removePreset = (idx) => setPresets(presets.filter((_, i) => i !== idx));
  const savePresets = async () => {
    const clean = presets.map((p) => ({ id: p.id, name: (p.name || "").trim(), rooms: (p.rooms || []).filter((r) => r.room_id && r.qty > 0) })).filter((p) => p.name && p.rooms.length);
    try { const { data } = await api.put("/settings", { cleaning_presets: clean }); setPresets(data.cleaning_presets || clean); toast.success("Presets saved"); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="max-w-2xl space-y-4">
      <div className="bg-[#2B4C3F]/[0.04] border border-[#2B4C3F]/15 rounded-md p-4">
        <p className="text-sm text-muted-foreground">Build a library of <strong className="text-foreground">rooms</strong> with their default cleaning tasks. On each property's Cleaning tab your team adds rooms from this library (e.g. add "Bathroom" twice for a 2-bath place) — the tasks come with them, and can be tweaked per property.</p>
      </div>
      <div className="space-y-3">
        <Reorder.Group axis="y" values={rooms} onReorder={setRooms} className="space-y-3">
          {rooms.map((r, i) => (
            <RoomEditor key={r.id || i} room={r} onChange={(nr) => updateRoom(i, nr)} onRemove={() => removeRoom(i)} />
          ))}
        </Reorder.Group>
        {rooms.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No rooms yet — add your first below.</p>}
      </div>
      <div className="flex gap-2">
        <Input data-testid="new-room-input" value={newRoom} onChange={(e) => setNewRoom(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addRoom()} placeholder="New room name (e.g. Master Bedroom)" />
        <Button variant="outline" data-testid="add-room-button" onClick={addRoom}><Plus className="h-4 w-4 mr-1" />Add room</Button>
      </div>
      <Button data-testid="save-rooms-button" onClick={save}><Save className="h-4 w-4 mr-1" />Save Cleaning Rooms</Button>

      <div className="pt-6 border-t border-border space-y-4">
        <div className="bg-[#D98324]/[0.05] border border-[#D98324]/20 rounded-md p-4">
          <p className="text-sm text-muted-foreground"><strong className="text-foreground">Presets</strong> bundle a set of rooms so staff can add a whole property layout in one tap (e.g. a "2-bed apartment" adds 2 bedrooms, a kitchen and a bathroom). Set how many of each room a preset includes, then save.</p>
        </div>
        <div className="space-y-3">
          {presets.map((p, i) => <PresetEditor key={p.id} preset={p} library={rooms} onChange={(np) => updatePreset(i, np)} onRemove={() => removePreset(i)} />)}
          {presets.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No presets yet.</p>}
        </div>
        <div className="flex gap-2">
          <Input data-testid="new-preset-input" value={newPreset} onChange={(e) => setNewPreset(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addPreset()} placeholder="New preset name (e.g. 3-bed house)" />
          <Button variant="outline" data-testid="add-preset-button" onClick={addPreset}><Plus className="h-4 w-4 mr-1" />Add preset</Button>
        </div>
        <Button data-testid="save-presets-button" onClick={savePresets}><Save className="h-4 w-4 mr-1" />Save Presets</Button>
      </div>
    </div>
  );
}


export default function Settings() {
  const { user } = useAuth();
  if (user.role !== "admin") return <div className="overline text-muted-foreground">Admins only.</div>;
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div>
        <p className="overline text-accent">Super-Admin</p>
        <h1 className="font-head text-3xl font-extrabold tracking-tight mt-1">Settings & CMS</h1>
      </div>
      <Tabs defaultValue="users">
        <TabsList>
          <TabsTrigger value="users" data-testid="settings-tab-users"><UsersIcon className="h-4 w-4 mr-1.5" />Users</TabsTrigger>
          <TabsTrigger value="template" data-testid="settings-tab-template"><ListChecks className="h-4 w-4 mr-1.5" />Checklist Template</TabsTrigger>
          <TabsTrigger value="global" data-testid="settings-tab-global"><SlidersHorizontal className="h-4 w-4 mr-1.5" />Global</TabsTrigger>
          <TabsTrigger value="contractors" data-testid="settings-tab-contractors"><HardHat className="h-4 w-4 mr-1.5" />Contractors & Cleaners</TabsTrigger>
          <TabsTrigger value="cleaning" data-testid="settings-tab-cleaning"><Sparkles className="h-4 w-4 mr-1.5" />Cleaning</TabsTrigger>
          <TabsTrigger value="emails" data-testid="settings-tab-emails"><Mail className="h-4 w-4 mr-1.5" />Emails</TabsTrigger>
          <TabsTrigger value="onboarding" data-testid="settings-tab-onboarding"><ClipboardList className="h-4 w-4 mr-1.5" />Onboarding Form</TabsTrigger>
        </TabsList>
        <TabsContent value="users" className="mt-5"><UsersTab /></TabsContent>
        <TabsContent value="template" className="mt-5"><TemplateTab /></TabsContent>
        <TabsContent value="global" className="mt-5"><SettingsTab /></TabsContent>
        <TabsContent value="contractors" className="mt-5"><ContractorsTab /><CleanersDirectory /></TabsContent>
        <TabsContent value="cleaning" className="mt-5"><CleaningTab /></TabsContent>
        <TabsContent value="emails" className="mt-5"><EmailsTab /></TabsContent>
        <TabsContent value="onboarding" className="mt-5"><OnboardingFormEditor /></TabsContent>
      </Tabs>
    </motion.div>
  );
}
